import { useEffect } from "react";

/**
 * Lightweight scroll-reveal animation for the landing pages only.
 *
 * The animation is applied to each section's inner content wrapper
 * (`.landing_container`), NOT the <section> itself — sections have coloured
 * backgrounds that must stay put, so only the content fades/slides in.
 * Content below the fold animates in as it scrolls into view; content already
 * visible on load is left untouched (no flash, no blank page).
 *
 * Implementation notes:
 *  - Uses a single rAF-throttled, passive scroll/resize listener and
 *    getBoundingClientRect — no extra dependencies, works in every browser,
 *    and self-removes once everything has been revealed.
 *  - Setup is deferred until the layout is stable (window `load` + rAF). Many
 *    sections use plain <img> without intrinsic sizing, so at mount the page is
 *    collapsed and every element measures as "in view"; waiting for load makes
 *    the fold measurement accurate.
 *  - Only below-the-fold wrappers get the `landing-reveal` (hidden) class, so
 *    already-visible content never flashes and is never hidden.
 *  - Graceful: with no JS or reduced-motion, the class is never added and every
 *    section stays fully visible.
 *
 * @param {boolean} enabled  Only true on the custom landing layout.
 * @param {string}  routeKey Re-runs the setup on client-side navigation.
 */
const REVEAL_SELECTOR = 'section[class^="landing_"] .landing_container';

export default function useLandingReveal(enabled, routeKey) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReduced) return undefined;

    let cleanupListeners = null;
    let rafId = null;

    const setup = () => {
      const targets = Array.from(document.querySelectorAll(REVEAL_SELECTOR));
      if (!targets.length) return;

      // Hide only what starts fully below the fold (avoids hiding/flashing
      // anything the user can already see on load).
      const pending = targets.filter((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight) return false;
        el.classList.add("landing-reveal");
        return true;
      });

      if (!pending.length) return;

      let ticking = false;

      const reveal = () => {
        ticking = false;
        const trigger = window.innerHeight * 0.9; // 10% into the viewport
        for (let i = pending.length - 1; i >= 0; i -= 1) {
          if (pending[i].getBoundingClientRect().top < trigger) {
            pending[i].classList.add("is-revealed");
            pending.splice(i, 1);
          }
        }
        if (!pending.length) cleanupListeners();
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(reveal);
      };

      cleanupListeners = () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      reveal(); // reveal anything already within the trigger line
    };

    // Run once the layout has settled so the fold measurement is accurate.
    const start = () => {
      rafId = window.requestAnimationFrame(() =>
        window.requestAnimationFrame(setup)
      );
    };

    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      window.removeEventListener("load", start);
      if (rafId) window.cancelAnimationFrame(rafId);
      if (cleanupListeners) cleanupListeners();
    };
  }, [enabled, routeKey]);
}
